import { formatDuration } from '../lib/audio';
import type { Track } from '../types';
import { Artwork } from './Artwork';
import { PlayIcon } from './PlayerIcons';

interface SongListProps {
  tracks: Track[];
  selectedIds?: string[];
  onToggleSelect?: (trackId: string) => void;
  onPlay?: (trackId: string) => void;
  onQueue?: (trackId: string) => void;
  actionsLabel?: string;
}

export function SongList({
  tracks,
  selectedIds = [],
  onToggleSelect,
  onPlay,
  onQueue,
  actionsLabel = 'Actions'
}: SongListProps) {
  if (!tracks.length) {
    return (
      <div className="panel rounded-[2rem] p-8 text-center text-foreground/70">
        No songs here yet. Upload a few tracks to start building your offline library.
      </div>
    );
  }

  return (
    <div className="panel overflow-hidden rounded-[2rem]">
      <div className="hidden grid-cols-[auto,1fr,auto] gap-4 border-b border-white/10 px-5 py-4 text-xs uppercase tracking-[0.24em] text-foreground/50 sm:grid">
        <span>Track</span>
        <span>Info</span>
        <span>{actionsLabel}</span>
      </div>
      <ul className="divide-y divide-white/5">
        {tracks.map((track) => {
          const selected = selectedIds.includes(track.id);
          return (
            <li
              key={track.id}
              className="grid gap-4 px-4 py-4 sm:grid-cols-[auto,1fr,auto] sm:px-5"
            >
              <div className="flex items-start gap-3 sm:contents">
                <label className="flex items-center gap-3 sm:gap-4">
                  {onToggleSelect ? (
                    <input
                      type="checkbox"
                      checked={selected}
                      onChange={() => onToggleSelect(track.id)}
                      aria-label={`Select ${track.title}`}
                      className="h-4 w-4 rounded border-white/20 bg-transparent"
                    />
                  ) : null}
                  <Artwork track={track} size="md" />
                </label>
                <div className="min-w-0 flex-1 sm:hidden">
                  <button
                    type="button"
                    onClick={() => onPlay?.(track.id)}
                    className="block truncate text-left text-lg font-semibold hover:text-accent"
                  >
                    {track.title}
                  </button>
                  <p className="mt-1 truncate text-sm text-foreground/70">
                    {track.artist} • {track.album}
                  </p>
                  <p className="mt-1 text-xs text-foreground/55">{formatDuration(track.duration)}</p>
                </div>
              </div>

              <div className="hidden min-w-0 sm:block">
                {onToggleSelect ? (
                  <span className="sr-only">selection visible in first column</span>
                ) : null}
                <button
                  type="button"
                  onClick={() => onPlay?.(track.id)}
                  className="truncate text-left font-semibold hover:text-accent"
                >
                  {track.title}
                </button>
                <p className="mt-1 truncate text-sm text-foreground/70">
                  {track.artist} • {track.album}
                </p>
                <p className="mt-2 text-xs text-foreground/55">{formatDuration(track.duration)}</p>
              </div>

              <div className="flex items-center gap-3 pl-[3.25rem] sm:self-center sm:pl-0">
                <button
                  type="button"
                  onClick={() => onPlay?.(track.id)}
                  className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-accent text-black"
                  aria-label={`Play ${track.title}`}
                >
                  <PlayIcon />
                </button>
                <button
                  type="button"
                  onClick={() => onQueue?.(track.id)}
                  className="rounded-full border border-white/10 bg-white/5 px-4 py-2 text-sm text-foreground/85 transition hover:bg-white/10"
                >
                  Add to queue
                </button>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
